import { Injectable } from '@angular/core';

export class IndexedDatabaseInstance {
  public constructor(
    private readonly db: IDBDatabase,
    private readonly dbStorage: string,
  ) {}

  public close(): void {
    this.db.close();
  }

  public async get<T>(key: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const transaction = this.db.transaction(this.dbStorage, 'readonly');
      const objectStore = transaction.objectStore(this.dbStorage);
      const request = objectStore.get(key);
      request.onsuccess = (event) => {
        resolve(request.result);
      };
      request.onerror = (event) => {
        reject(request.error);
      };
    });
  }

  public async getAllKeys(): Promise<IDBValidKey[]> {
    return new Promise<IDBValidKey[]>((resolve, reject) => {
      const transaction = this.db.transaction(this.dbStorage, 'readonly');
      const objectStore = transaction.objectStore(this.dbStorage);
      const request = objectStore.getAllKeys();
      request.onsuccess = (event) => {
        resolve(request.result);
      };
      request.onerror = (event) => {
        reject(request.error);
      };
    });
  }

  public async getAll<T>(): Promise<T[]> {
    return new Promise<T[]>((resolve, reject) => {
      const transaction = this.db.transaction(this.dbStorage, 'readonly');
      const objectStore = transaction.objectStore(this.dbStorage);
      const request = objectStore.getAll();
      request.onsuccess = (event) => {
        resolve(request.result);
      };
      request.onerror = (event) => {
        reject(request.error);
      };
    });
  }

  public async put<T>(key: string, value: T): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const transaction = this.db.transaction(this.dbStorage, 'readwrite');
      const objectStore = transaction.objectStore(this.dbStorage);
      const request = objectStore.put(value, key);
      request.onsuccess = (event) => {
        resolve();
      };
      request.onerror = (event) => {
        reject(request.error);
      };
    });
  }

  public async delete<T>(key: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const transaction = this.db.transaction(this.dbStorage, 'readwrite');
      const objectStore = transaction.objectStore(this.dbStorage);
      const request = objectStore.delete(key);
      request.onsuccess = (event) => {
        resolve();
      };
      request.onerror = (event) => {
        reject(request.error);
      };
    });
  }

  public async clear<T>(key: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const transaction = this.db.transaction(this.dbStorage, 'readwrite');
      const objectStore = transaction.objectStore(this.dbStorage);
      const request = objectStore.clear();
      request.onsuccess = (event) => {
        resolve();
      };
      request.onerror = (event) => {
        reject(request.error);
      };
    });
  }
}

@Injectable({ providedIn: 'root' })
export class IndexedDbService {
  public async openDatabase(
    dbName: string,
    dbStorage: string,
    migrationPlans: Record<number, (db: IDBDatabase) => void>,
    version: number = 1,
  ): Promise<{ db: IndexedDatabaseInstance; dbStorage: string }> {
    const dbObject = await this.open(dbName, dbStorage, migrationPlans, version);

    return dbObject;
  }

  private async open(
    dbName: string,
    dbStorage: string,
    migrationPlans: Record<number, (db: IDBDatabase) => void>,
    version: number = 1,
  ): Promise<{ db: IndexedDatabaseInstance; dbStorage: string }> {
    return new Promise<{ db: IndexedDatabaseInstance; dbStorage: string }>((resolve, reject) => {
      const request = indexedDB.open(dbName, version);
      request.onerror = (event) => {
        reject(request.error);
      };
      request.onsuccess = (event) => {
        const db = request.result;
        resolve({ db: new IndexedDatabaseInstance(db, dbStorage), dbStorage });
      };
      request.onupgradeneeded = (event) => {
        const db = request.result;
        const migrationPlan = migrationPlans[version];

        migrationPlan(db);
      };
    });
  }

  private async delete(dbName: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.deleteDatabase(dbName);
      request.onerror = (event) => {
        reject(request.error);
      };
      request.onsuccess = (event) => {
        resolve();
      };
    });
  }
}
